import { createSelector } from 'reselect';
import memoize from 'lodash/memoize';

import { selectUser } from '@pressreader/authentication-core';
import { selectCurrentSubscription } from '@pressreader/commerce';
import { selectIssueRestrictionsByCidDateKeySelector } from '@pressreader/content';
import type { IArticle } from '@pressreader/content-types';
import { extractCidDateKeyString } from '@pressreader/content-utils';
import { getSponsorshipStatus } from '@pressreader/sponsorship-core';
import { SponsorshipStatus } from '@pressreader/sponsorship-types';

import { selectArticleTranslationConfig } from '../../global.selectors';
import { ArticleRestrictions } from '../../types';

import { selectArticleByIdAndLanguageSelector } from './articles';

const selectArticleTranslationEnabledFromConfig = createSelector(
    selectUser,
    selectCurrentSubscription,
    getSponsorshipStatus,
    selectArticleTranslationConfig,
    (user, subscription, sponsorStatus, translationConfig) => {
        if (sponsorStatus === SponsorshipStatus.On) {
            return translationConfig.enableForSponsored;
        }
        if (!user.isAuthenticated) {
            return translationConfig.enableForAnon;
        }
        if (subscription && subscription.isUnlimited) {
            return translationConfig.enableForPremium;
        }
        if (subscription && subscription.isFree) {
            return translationConfig.enableForRegisteredPayG;
        }

        return translationConfig.enable;
    },
);

const selectArticleRestrictionsByArticleIdSelector = createSelector(
    selectArticleTranslationEnabledFromConfig,
    selectIssueRestrictionsByCidDateKeySelector,
    selectArticleByIdAndLanguageSelector,
    (translationEnabled, selectIssueRestrictionsByCidDateKey, selectArticleByIdAndLanguage) =>
        memoize(
            (
                params:
                    | {
                          articleId: IArticle['id'];
                          language: IArticle['language'] | null;
                      }
                    | { article: IArticle },
            ) => {
                const isParamsWithArticle = 'article' in params;
                const articleId = isParamsWithArticle ? params.article.id : params.articleId;
                const language = isParamsWithArticle ? params.article.language : params.language;
                const article = isParamsWithArticle ? params.article : selectArticleByIdAndLanguage(articleId, language);
                let articleIssue = article?.issue;

                if (!article) {
                    return null;
                }

                const restrictions: ArticleRestrictions = {
                    onlineArticlePrinting: false,
                    translation: !translationEnabled,
                    sound: false,
                };

                if (!articleIssue) {
                    // For posts and articles without issue, all restrictions are disabled except translation
                    const article = selectArticleByIdAndLanguage(articleId, language);
                    articleIssue = article?.issue;
                }
                if (articleIssue) {
                    const cidDateKey = extractCidDateKeyString(articleIssue.issueKey);
                    const issueRestrictions = selectIssueRestrictionsByCidDateKey(cidDateKey);
                    restrictions.translation = restrictions.translation || issueRestrictions.translation;
                    restrictions.onlineArticlePrinting = restrictions.onlineArticlePrinting || issueRestrictions.onlineArticlePrinting;
                    restrictions.sound = issueRestrictions.sound;
                } else {
                    restrictions.translation = true; // translation is not available for posts
                    restrictions.sound = true;
                }

                return restrictions;
            },
        ),
);

export { selectArticleRestrictionsByArticleIdSelector };
